import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import api, { API_ENDPOINTS } from '../config/api';
import toast from 'react-hot-toast';
import { useForm, Controller } from 'react-hook-form';
import ProfessionalRichTextEditor from '../components/ProfessionalRichTextEditor';
import {
  ArrowLeft,
  DollarSign,
  Clock,
  MapPin,
  Tag,
  FileText,
  Users,
  Calendar,
  Plus,
  X,
  CheckCircle
} from 'lucide-react';

const EditGigPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch gig details
  const { data: gig, isLoading, error } = useQuery(
    ['gig', id],
    () => api.get(API_ENDPOINTS.GIGS.DETAIL(id)),
    {
      select: (data) => data.data,
      enabled: !!id,
    }
  );

  const { register, handleSubmit, formState: { errors }, setValue, watch, control } = useForm();

  // State for image and offers
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [includes, setIncludes] = useState(['']);
  const [skills, setSkills] = useState(['']);
  const [portfolio, setPortfolio] = useState([{ description: '', imageUrl: '', videoUrl: '' }]);
  
  // Basic/Premium offers state
  const [basicOffer, setBasicOffer] = useState({
    price: '',
    deliveryValue: '',
    deliveryUnit: 'days',
    pros: [''],
    cons: ['']
  });
  const [premiumOffer, setPremiumOffer] = useState({
    price: '',
    deliveryValue: '',
    deliveryUnit: 'days',
    pros: [''],
    cons: ['']
  });

  // Update gig mutation with FormData
  const updateGigMutation = useMutation(
    (formData) => {
      return api.put(API_ENDPOINTS.GIGS.UPDATE(id), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    {
      onSuccess: () => {
        toast.success('Gig updated successfully!');
        queryClient.invalidateQueries(['gig', id]);
        navigate(`/gigs/${id}`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update gig');
      }
    }
  );

  // Helper functions for offers (same as CreateGigPage)
  const addInclude = () => setIncludes([...includes, '']);
  const removeInclude = (index) => {
    if (includes.length > 1) {
      setIncludes(includes.filter((_, i) => i !== index));
    }
  };
  const updateInclude = (index, value) => {
    const newIncludes = [...includes];
    newIncludes[index] = value;
    setIncludes(newIncludes);
  };

  const addSkill = () => setSkills([...skills, '']);
  const removeSkill = (index) => {
    if (skills.length > 1) {
      setSkills(skills.filter((_, i) => i !== index));
    }
  };
  const updateSkill = (index, value) => {
    const newSkills = [...skills];
    newSkills[index] = value;
    setSkills(newSkills);
  };

  const addPortfolioItem = () => {
    setPortfolio([...portfolio, { description: '', imageUrl: '', videoUrl: '' }]);
  };

  const removePortfolioItem = (index) => {
    if (portfolio.length > 1) {
      setPortfolio(portfolio.filter((_, i) => i !== index));
    }
  };

  const updatePortfolioItem = (index, field, value) => {
    const newPortfolio = [...portfolio];
    newPortfolio[index][field] = value;
    setPortfolio(newPortfolio);
  };

  const addPro = (offerType) => {
    if (offerType === 'basic') {
      setBasicOffer(prev => ({ ...prev, pros: [...prev.pros, ''] }));
    } else {
      setPremiumOffer(prev => ({ ...prev, pros: [...prev.pros, ''] }));
    }
  };
  const removePro = (offerType, index) => {
    if (offerType === 'basic') {
      setBasicOffer(prev => ({ ...prev, pros: prev.pros.filter((_, i) => i !== index) }));
    } else {
      setPremiumOffer(prev => ({ ...prev, pros: prev.pros.filter((_, i) => i !== index) }));
    }
  };
  const updatePro = (offerType, index, value) => {
    if (offerType === 'basic') {
      const newPros = [...basicOffer.pros];
      newPros[index] = value;
      setBasicOffer(prev => ({ ...prev, pros: newPros }));
    } else {
      const newPros = [...premiumOffer.pros];
      newPros[index] = value;
      setPremiumOffer(prev => ({ ...prev, pros: newPros }));
    }
  };
  const addCon = (offerType) => {
    if (offerType === 'basic') {
      setBasicOffer(prev => ({ ...prev, cons: [...prev.cons, ''] }));
    } else {
      setPremiumOffer(prev => ({ ...prev, cons: [...prev.cons, ''] }));
    }
  };
  const removeCon = (offerType, index) => {
    if (offerType === 'basic') {
      setBasicOffer(prev => ({ ...prev, cons: prev.cons.filter((_, i) => i !== index) }));
    } else {
      setPremiumOffer(prev => ({ ...prev, cons: prev.cons.filter((_, i) => i !== index) }));
    }
  };
  const updateCon = (offerType, index, value) => {
    if (offerType === 'basic') {
      const newCons = [...basicOffer.cons];
      newCons[index] = value;
      setBasicOffer(prev => ({ ...prev, cons: newCons }));
    } else {
      const newCons = [...premiumOffer.cons];
      newCons[index] = value;
      setPremiumOffer(prev => ({ ...prev, cons: newCons }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Populate form when gig data is loaded
  useEffect(() => {
    if (gig) {
      setValue('title', gig.title);
      setValue('description', gig.description);
      setValue('category', gig.category);
      setValue('type', gig.type);
      
      // Set image preview from existing imageUrl
      if (gig.imageUrl) {
        setImagePreview(gig.imageUrl);
      }
      
      // Populate basic/premium offers
      if (gig.pricing?.basic && typeof gig.pricing.basic === 'object') {
        setBasicOffer({
          price: gig.pricing.basic.price || '',
          deliveryValue: gig.pricing.basic.deliveryTime?.value || '',
          deliveryUnit: gig.pricing.basic.deliveryTime?.unit || 'days',
          pros: gig.pricing.basic.pros && gig.pricing.basic.pros.length > 0 ? gig.pricing.basic.pros : [''],
          cons: gig.pricing.basic.cons && gig.pricing.basic.cons.length > 0 ? gig.pricing.basic.cons : ['']
        });
      }
      if (gig.pricing?.premium && typeof gig.pricing.premium === 'object') {
        setPremiumOffer({
          price: gig.pricing.premium.price || '',
          deliveryValue: gig.pricing.premium.deliveryTime?.value || '',
          deliveryUnit: gig.pricing.premium.deliveryTime?.unit || 'days',
          pros: gig.pricing.premium.pros && gig.pricing.premium.pros.length > 0 ? gig.pricing.premium.pros : [''],
          cons: gig.pricing.premium.cons && gig.pricing.premium.cons.length > 0 ? gig.pricing.premium.cons : ['']
        });
      }
      
      // Populate arrays
      setIncludes(gig.includes && gig.includes.length > 0 ? gig.includes : ['']);
      setSkills(gig.skills && gig.skills.length > 0 ? gig.skills : ['']);
      setPortfolio(gig.portfolio && gig.portfolio.length > 0 ? gig.portfolio : [{ description: '', imageUrl: '', videoUrl: '' }]);
    }
  }, [gig, setValue]);

  const onSubmit = (data) => {
    // Validate basic and premium offers
    if (!basicOffer.price || !premiumOffer.price) {
      toast.error('Both basic and premium offers are required with prices');
      return;
    }

    // Image is optional for editing (only required if gig has no existing image)
    if (!selectedImage && !gig?.imageUrl) {
      toast.error('Please upload a gig image');
      return;
    }

    // Build pricing object with basic/premium offers
    const pricing = {
      basic: {
        price: parseFloat(basicOffer.price),
        deliveryTime: {
          value: parseInt(basicOffer.deliveryValue) || 1,
          unit: basicOffer.deliveryUnit
        },
        pros: basicOffer.pros.filter(p => p.trim() !== ''),
        cons: basicOffer.cons.filter(c => c.trim() !== '')
      },
      premium: {
        price: parseFloat(premiumOffer.price),
        deliveryTime: {
          value: parseInt(premiumOffer.deliveryValue) || 1,
          unit: premiumOffer.deliveryUnit
        },
        pros: premiumOffer.pros.filter(p => p.trim() !== ''),
        cons: premiumOffer.cons.filter(c => c.trim() !== '')
      }
    };

    // Create FormData for file upload
    const formData = new FormData();
    
    // Append all text fields
    formData.append('title', data.title);
    formData.append('description', data.description);
    formData.append('category', data.category);
    formData.append('type', data.type);
    formData.append('pricing', JSON.stringify(pricing));
    
    // Append arrays
    formData.append('includes', JSON.stringify(includes.filter(inc => inc.trim() !== '')));
    formData.append('skills', JSON.stringify(skills.filter(skill => skill.trim() !== '')));
    formData.append('portfolio', JSON.stringify(portfolio.filter(item => item.description.trim() !== '')));

    // Append image file only if new image was selected
    if (selectedImage) {
      formData.append('image', selectedImage);
    }

    updateGigMutation.mutate(formData);
  };

  const deliveryUnits = ['hours', 'days', 'weeks'];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (error || !gig) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-6xl mb-4">❌</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Gig not found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            The gig you're trying to edit doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate('/gigs')}
            className="btn-primary"
          >
            Browse Gigs
          </button>
        </div>
      </div>
    );
  }

  // Check if user is the gig owner
  if (gig.talent._id !== user?._id) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🚫</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            You can only edit gigs that you created.
          </p>
          <button
            onClick={() => navigate(`/gigs/${id}`)}
            className="btn-primary"
          >
            View Gig
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => navigate(`/gigs/${id}`)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-heading font-bold text-heading">
              Edit Gig
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Update your service offering details
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Gig Title *
                </label>
                <input
                  {...register('title', { required: 'Gig title is required' })}
                  type="text"
                  className="w-full input-field"
                  placeholder="e.g., Professional Logo Design"
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Gig Description *
                </label>
                <Controller
                  name="description"
                  control={control}
                  rules={{ required: 'Gig description is required' }}
                  render={({ field }) => (
                      <ProfessionalRichTextEditor
                      value={field.value || ''}
                      onChange={field.onChange}
                      placeholder="Describe your service, what you offer, and what clients can expect. You can format text, add headings, links, images, videos, and tables..."
                    />
                  )}
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category *
                </label>
                <select
                  {...register('category', { required: 'Category is required' })}
                  className="w-full input-field"
                >
                  <option value="">Select a category</option>
                  <option value="web-development">Web Development</option>
                  <option value="mobile-development">Mobile Development</option>
                  <option value="design">Design</option>
                  <option value="writing">Writing</option>
                  <option value="marketing">Marketing</option>
                  <option value="data-science">Data Science</option>
                  <option value="other">Other</option>
                </select>
                {errors.category && (
                  <p className="text-red-500 text-sm mt-1">{errors.category.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Gig Type *
                </label>
                <select
                  {...register('type', { required: 'Gig type is required' })}
                  className="w-full input-field"
                >
                  <option value="">Select gig type</option>
                  <option value="professional">Professional Services</option>
                  <option value="labour">Physical Labor</option>
                </select>
                {errors.type && (
                  <p className="text-red-500 text-sm mt-1">{errors.type.message}</p>
                )}
              </div>

              {/* Gig Image Upload */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Gig Image {gig?.imageUrl ? '(Optional - leave empty to keep current)' : '(Required)'}
                </label>
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-accent file:text-white hover:file:bg-accent-dark"
                  />
                  {imagePreview && (
                    <div className="mt-2">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                      />
                    </div>
                  )}
                  {!imagePreview && gig?.imageUrl && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Current image will be kept if no new image is uploaded
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Basic Offer */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Basic Offer *
            </h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Price ($) *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={basicOffer.price}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow positive integers
                      if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                        setBasicOffer(prev => ({ ...prev, price: value }));
                      }
                    }}
                    className="w-full input-field"
                    placeholder="100"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Delivery Time *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={basicOffer.deliveryValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow positive integers
                      if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                        setBasicOffer(prev => ({ ...prev, deliveryValue: value }));
                      }
                    }}
                    className="w-full input-field"
                    placeholder="3"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Unit *
                  </label>
                  <select
                    value={basicOffer.deliveryUnit}
                    onChange={(e) => setBasicOffer(prev => ({ ...prev, deliveryUnit: e.target.value }))}
                    className="w-full input-field"
                    required
                  >
                    {deliveryUnits.map(unit => (
                      <option key={unit} value={unit}>
                        {unit.charAt(0).toUpperCase() + unit.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Pros
                  </label>
                  <div className="space-y-2">
                    {basicOffer.pros.map((pro, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={pro}
                          onChange={(e) => updatePro('basic', index, e.target.value)}
                          className="flex-1 input-field"
                          placeholder="Enter a pro..."
                        />
                        {basicOffer.pros.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePro('basic', index)}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addPro('basic')}
                      className="flex items-center text-accent hover:text-accent/80 text-sm"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Pro
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cons
                  </label>
                  <div className="space-y-2">
                    {basicOffer.cons.map((con, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={con}
                          onChange={(e) => updateCon('basic', index, e.target.value)}
                          className="flex-1 input-field"
                          placeholder="Enter a con..."
                        />
                        {basicOffer.cons.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeCon('basic', index)}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addCon('basic')}
                      className="flex items-center text-accent hover:text-accent/80 text-sm"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Con
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Premium Offer */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Premium Offer *
            </h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Price ($) *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={premiumOffer.price}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow positive integers
                      if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                        setPremiumOffer(prev => ({ ...prev, price: value }));
                      }
                    }}
                    className="w-full input-field"
                    placeholder="200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Delivery Time *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={premiumOffer.deliveryValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow positive integers
                      if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                        setPremiumOffer(prev => ({ ...prev, deliveryValue: value }));
                      }
                    }}
                    className="w-full input-field"
                    placeholder="1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Unit *
                  </label>
                  <select
                    value={premiumOffer.deliveryUnit}
                    onChange={(e) => setPremiumOffer(prev => ({ ...prev, deliveryUnit: e.target.value }))}
                    className="w-full input-field"
                    required
                  >
                    {deliveryUnits.map(unit => (
                      <option key={unit} value={unit}>
                        {unit.charAt(0).toUpperCase() + unit.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Pros
                  </label>
                  <div className="space-y-2">
                    {premiumOffer.pros.map((pro, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={pro}
                          onChange={(e) => updatePro('premium', index, e.target.value)}
                          className="flex-1 input-field"
                          placeholder="Enter a pro..."
                        />
                        {premiumOffer.pros.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePro('premium', index)}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addPro('premium')}
                      className="flex items-center text-accent hover:text-accent/80 text-sm"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Pro
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cons
                  </label>
                  <div className="space-y-2">
                    {premiumOffer.cons.map((con, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={con}
                          onChange={(e) => updateCon('premium', index, e.target.value)}
                          className="flex-1 input-field"
                          placeholder="Enter a con..."
                        />
                        {premiumOffer.cons.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeCon('premium', index)}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addCon('premium')}
                      className="flex items-center text-accent hover:text-accent/80 text-sm"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Con
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Location
            </h2>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  {...register('location.remote')}
                  type="checkbox"
                  className="h-4 w-4 text-accent focus:ring-accent border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Remote work available
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    City
                  </label>
                  <input
                    {...register('location.city')}
                    type="text"
                    className="w-full input-field"
                    placeholder="e.g., New York"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Country
                  </label>
                  <input
                    {...register('location.country')}
                    type="text"
                    className="w-full input-field"
                    placeholder="e.g., United States"
                  />
                </div>
              </div>
            </div>
          </div>


          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(`/gigs/${id}`)}
              className="btn-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateGigMutation.isLoading}
              className="btn-primary"
            >
              {updateGigMutation.isLoading ? 'Updating...' : 'Update Gig'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditGigPage;
